/*
 * taskpane.js — TaskPane unifiée FDWeb Pack 5 (v1.4.3 BUNDLE_VERSION_MARKER_143)
 * ============================================================================
 * Une seule TaskPane avec barre de navigation supérieure (4 onglets) :
 *   1. Premier contact  → POST W1 (BCE + validation)
 *   2. Suivi de dossier → POST W3 (création directe Odoo)
 *   3. Historique       → liste avec sous-onglets primo/suivi + Export/Import/Vider
 *   4. Configuration    → Client ID + URLs webhook + Save + Export/Import
 *
 * L'onglet initial est lu depuis window.PACK5_INITIAL_TAB (set par le HTML wrapper).
 * Pinning activé via le manifest (SupportsPinning) — la TaskPane reste ouverte
 * tant que l'utilisateur ne la ferme pas.
 */

const CONFIG = {
  primo: {
    title: "Premier contact",
    subtitle: "Recherche BCE et création après validation",
    defaultUrl: "https://n8n.fdwebpro.be/webhook/pack5-mail-bce",
    settingsUrlKey: "webhookUrl",
    actionHeader: "primo",
    historyKey: "pack5_primo_history",
    expectsJsonResponse: true,
    loaderText: "Analyse du mail et recherche BCE…"
  },
  followup: {
    title: "Suivi de dossier",
    subtitle: "Création directe dans Odoo",
    defaultUrl: "https://n8n.fdwebpro.be/webhook/pack5-mail-followup",
    settingsUrlKey: "webhookFollowUpUrl",
    actionHeader: "follow_up",
    historyKey: "pack5_followup_history",
    expectsJsonResponse: false,
    loaderText: "Recherche dans Odoo…"
  }
};

const HISTORY_MAX = 50;
const REQUEST_TIMEOUT_MS = 90000;
const EXPORT_VERSION = "1.4.0";

let currentTab = "primo";
let historySubTab = "primo"; // pour l'onglet Historique

/* ===========================================================================
 * INIT
 * ========================================================================= */
function initApp() {
  try {
    bindTabNav();
    bindConfigForm();
    bindHistoryActions();

    // Handler ItemChanged : nécessaire pour TaskPane épinglée (SupportsPinning).
    // Sans ce handler, Office.context.mailbox.item peut rester sur l'ancien mail
    // quand l'utilisateur change de sélection. À chaque changement, on revient
    // à l'écran d'accueil pour que l'utilisateur choisisse l'action sur ce mail.
    if (Office.context && Office.context.mailbox && Office.context.mailbox.addHandlerAsync) {
      try {
        Office.context.mailbox.addHandlerAsync(
          Office.EventType.ItemChanged,
          onItemChanged
        );
      } catch (e) {
        // Outlook web ou versions anciennes peuvent ne pas supporter ce handler
        console.warn("ItemChanged handler non disponible :", e && e.message);
      }
    }

    const initial = (typeof window !== "undefined" && window.PACK5_INITIAL_TAB) || "primo";
    switchTab(initial);
  } catch (err) {
    // Affiche l'erreur visiblement plutôt qu'un panneau silencieusement vide
    const main = document.querySelector(".tab-section") || document.body;
    if (main) {
      main.style.display = "block";
      main.innerHTML = `<div class="status status-error" style="margin:12px">Erreur d'initialisation : ${(err && err.message) || err}</div>`;
    }
  }
}

/**
 * Appelé quand l'utilisateur change de mail dans Outlook alors que la TaskPane
 * est épinglée. On revient à l'accueil — l'utilisateur clique ensuite sur l'onglet
 * désiré pour traiter le nouveau mail.
 */
function onItemChanged() {
  // Reset de l'UI : retour à l'accueil
  switchTab("home");
  // Vide les zones de résultat de tous les onglets pour éviter qu'un ancien
  // résultat reste affiché si l'utilisateur clique à nouveau sur l'onglet
  document.querySelectorAll(".tab-section .result-zone").forEach((el) => {
    el.innerHTML = "";
    el.style.display = "none";
  });
  document.querySelectorAll(".tab-section .status-zone").forEach((el) => {
    el.style.display = "none";
  });
}

/* eslint-disable-next-line no-undef */
if (typeof Office !== "undefined" && Office.onReady) {
  Office.onReady(() => initApp());
} else if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initApp);
} else {
  initApp();
}

/* ===========================================================================
 * NAVIGATION ONGLETS
 * ========================================================================= */
function bindTabNav() {
  document.querySelectorAll(".nav-tab").forEach((btn) => {
    btn.addEventListener("click", () => switchTab(btn.dataset.tab));
  });
}

function switchTab(tabName) {
  currentTab = tabName;

  document.querySelectorAll(".nav-tab").forEach((b) => {
    b.classList.toggle("active", b.dataset.tab === tabName);
  });
  document.querySelectorAll(".tab-section").forEach((s) => {
    s.style.display = s.dataset.section === tabName ? "" : "none";
  });

  if (tabName === "primo" || tabName === "followup") {
    runAction(tabName);
  } else if (tabName === "invoice") {
    renderInvoiceView();
  } else if (tabName === "client") {
    renderClientView();
  } else if (tabName === "history") {
    renderHistoryView();
  } else if (tabName === "config") {
    loadConfigForm();
  }
  // tabName === "home" : rien à faire, juste afficher l'écran d'accueil
}

/* ===========================================================================
 * ACTION : POST WEBHOOK (PRIMO ou FOLLOWUP)
 * ========================================================================= */
async function runAction(tabName) {
  const cfg = CONFIG[tabName];
  const item = Office.context.mailbox.item;

  if (!item || !item.from) {
    showStatus(tabName, "info", "Aucun mail sélectionné. Ouvrez un mail puis cliquez à nouveau sur cet onglet.");
    return;
  }

  const config = readConfig();
  if (!config.clientId) {
    showStatus(tabName, "error", "Configuration manquante — onglet « Configuration » : saisissez votre Client ID.");
    return;
  }

  const webhookUrl = config[cfg.settingsUrlKey] || cfg.defaultUrl;
  showLoader(tabName, cfg.loaderText);

  try {
    const payload = await buildPayload(item, cfg.actionHeader);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    const response = await fetch(webhookUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Pack5-Client-Id": config.clientId,
        "X-Pack5-Token": config.bearerToken,
        "X-Pack5-Source": "outlook-addin-v1.4",
        "X-Pack5-Action": cfg.actionHeader
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });
    clearTimeout(timer);

    const status = response.status;
    const bodyText = await response.text();

    if (response.ok) {
      if (cfg.expectsJsonResponse) {
        let data;
        try { data = JSON.parse(bodyText); } catch { data = null; }
        if (Array.isArray(data)) data = data[0];
        if (data && data.status === "ready_to_validate") {
          renderPrimoCandidates(tabName, data, payload);
          addToHistory(cfg.historyKey, {
            ts: Date.now(),
            mail_subject: payload.subject,
            from_email: payload.from_email,
            status: "primo_pending",
            summary: `${(data.candidates || []).length} candidat(s) BCE — en attente de choix`
          });
        } else {
          showStatus(tabName, "info", "Réponse reçue mais format inattendu — vérifiez le workflow W1.");
        }
      } else {
        if (bodyText.trim().startsWith("<")) {
          renderHtml(tabName, bodyText);
        } else {
          showStatus(tabName, "success", "OK");
        }
        addToHistory(cfg.historyKey, {
          ts: Date.now(),
          mail_subject: payload.subject,
          from_email: payload.from_email,
          status: "success",
          summary: "Suivi enregistré",
          odoo_url: extractOdooLink(bodyText)
        });
      }
    } else if (status === 404 && cfg.actionHeader === "follow_up") {
      if (bodyText.trim().startsWith("<")) {
        renderHtml(tabName, bodyText);
      } else {
        showStatus(tabName, "error", "Société introuvable dans Odoo — utilisez « Premier contact ».");
      }
      addToHistory(cfg.historyKey, {
        ts: Date.now(),
        mail_subject: payload.subject,
        from_email: payload.from_email,
        status: "not_found",
        summary: "Société introuvable"
      });
    } else if (status >= 400 && status < 500) {
      showStatus(tabName, "error", `Erreur configuration (HTTP ${status}). Vérifiez votre Client ID.`);
      addToHistory(cfg.historyKey, { ts: Date.now(), mail_subject: payload.subject, from_email: payload.from_email, status: "config_error", summary: `HTTP ${status}` });
    } else {
      showStatus(tabName, "error", `Erreur serveur (HTTP ${status}). Réessayez dans quelques minutes.`);
      addToHistory(cfg.historyKey, { ts: Date.now(), mail_subject: payload.subject, from_email: payload.from_email, status: "server_error", summary: `HTTP ${status}` });
    }
  } catch (err) {
    if (err && err.name === "AbortError") {
      showStatus(tabName, "error", "Délai d'attente dépassé. Vérifiez votre connexion.");
    } else {
      const msg = (err && err.message) ? err.message : "erreur inconnue";
      showStatus(tabName, "error", `Erreur : ${msg}`);
    }
    addToHistory(cfg.historyKey, {
      ts: Date.now(),
      mail_subject: (Office.context.mailbox.item && Office.context.mailbox.item.subject) || "?",
      from_email: "?",
      status: "exception",
      summary: (err && err.message) || "exception"
    });
  }
}

/* ===========================================================================
 * RENDU CANDIDATS BCE (PRIMO)
 * ========================================================================= */
function renderPrimoCandidates(tabName, data, originalPayload) {
  hideLoader(tabName);
  const container = document.querySelector(`.tab-section[data-section="${tabName}"] .result-zone`);
  container.innerHTML = "";
  container.style.display = "";

  const ex = data.extracted || {};
  const candidates = data.candidates || [];
  const score = data.lead_score || null;

  // Badge de score si fourni par W1
  let scoreBadge = "";
  if (score && typeof score.score === "number") {
    const tier = score.tier || "cold";
    const tierLabel = tier === "hot" ? "🔥 HOT" : (tier === "warm" ? "☀️ WARM" : "❄️ COLD");
    const b = score.budget || {};
    const a = score.authority || {};
    const n = score.need || {};
    const t = score.timeline || {};
    scoreBadge = `<div class="lead-score lead-score-${tier}">
      <div class="lead-score-row">
        <span class="lead-score-num">${score.score}</span>
        <span class="lead-score-label">${tierLabel}</span>
        <span class="lead-score-bant">B${b.score || 0} · A${a.score || 0} · N${n.score || 0} · T${t.score || 0}</span>
        <span class="lead-score-info" aria-label="Plus d'infos">ℹ️</span>
      </div>
      <div class="lead-score-tooltip">
        <div class="tip-title">Score qualification BANT</div>
        ${score.summary ? `<div class="tip-summary">${escapeHtml(score.summary)}</div>` : ""}
        <ul class="tip-bant">
          <li><strong>Budget</strong> <span class="tip-pts">${b.score || 0}/25</span><br>${escapeHtml(b.justification || "—")}</li>
          <li><strong>Authority</strong> <span class="tip-pts">${a.score || 0}/25</span><br>${escapeHtml(a.justification || "—")}</li>
          <li><strong>Need</strong> <span class="tip-pts">${n.score || 0}/25</span><br>${escapeHtml(n.justification || "—")}</li>
          <li><strong>Timeline</strong> <span class="tip-pts">${t.score || 0}/25</span><br>${escapeHtml(t.justification || "—")}</li>
        </ul>
        <div class="tip-legend">
          <div><span class="tip-tier-hot">🔥 HOT</span> ≥ 70 — Lead chaud, à traiter en priorité</div>
          <div><span class="tip-tier-warm">☀️ WARM</span> 40–69 — Intéressant, à qualifier</div>
          <div><span class="tip-tier-cold">❄️ COLD</span> &lt; 40 — Peu actionnable</div>
        </div>
        <div class="tip-foot">Méthode BANT : Budget · Authority · Need · Timeline</div>
      </div>
    </div>`;
  }

  const info = document.createElement("div");
  info.className = "primo-extract";
  info.innerHTML = `
    <div class="primo-section-title">Société évoquée dans le mail</div>
    <div class="primo-extract-name">${escapeHtml(ex.company_name || "?")}</div>
    <div class="primo-extract-meta">
      ${escapeHtml(ex.person_name || "?")} &middot; ${escapeHtml(ex.person_email || "?")}
    </div>
    ${ex.context ? `<div class="primo-extract-context">${escapeHtml(ex.context)}</div>` : ""}
    ${scoreBadge}
  `;
  container.appendChild(info);

  const candidatesBlock = document.createElement("div");
  candidatesBlock.className = "primo-candidates";
  candidatesBlock.innerHTML = `<div class="primo-section-title">Quelle société valider ? <span class="primo-strategy">(stratégie : ${escapeHtml(data.bce_search_strategy || "?")})</span></div>`;

  if (candidates.length === 0) {
    candidatesBlock.innerHTML += `<div class="primo-no-candidate">Aucune correspondance BCE.</div>`;
  } else {
    candidates.forEach((c) => {
      const btn = document.createElement("button");
      btn.className = "primo-candidate-btn";
      btn.innerHTML = `
        <div class="primo-cand-name">
          ${escapeHtml(c.denomination || "?")}
          <span class="primo-cand-form">${escapeHtml(c.juridical_form_short || "?")}</span>
          ${c.score ? `<span class="primo-cand-score">Match ${c.score}%</span>` : ""}
        </div>
        <div class="primo-cand-meta">📍 ${escapeHtml(c.address || "?")}</div>
        <div class="primo-cand-meta">📊 ${escapeHtml(c.nace_main || "?")} · TVA ${escapeHtml(c.cbe_number_formatted || c.cbe_number || "?")}</div>
      `;
      btn.addEventListener("click", () => validateChoice(tabName, data, c.index, originalPayload));
      candidatesBlock.appendChild(btn);
    });
  }

  const rejectBtn = document.createElement("button");
  rejectBtn.className = "primo-reject-btn";
  rejectBtn.textContent = "✗ Aucune / création manuelle";
  rejectBtn.addEventListener("click", () => renderManualForm(tabName, data, originalPayload));
  candidatesBlock.appendChild(rejectBtn);

  container.appendChild(candidatesBlock);
}

/* ===========================================================================
 * VALIDATION D'UN CHOIX (W2)
 * ========================================================================= */
async function validateChoice(tabName, data, candidateIndex, originalPayload) {
  showLoader(tabName, candidateIndex === -1 ? "Marquage en traitement manuel…" : "Création dans Odoo en cours…");
  const url = data.validation_url_template.replace("{candidate}", candidateIndex);
  try {
    const response = await fetch(url, { method: "GET" });
    const bodyText = await response.text();
    if (response.ok && bodyText.trim().startsWith("<")) {
      renderHtml(tabName, bodyText);
      addToHistory(CONFIG.primo.historyKey, {
        ts: Date.now(),
        mail_subject: originalPayload.subject,
        from_email: originalPayload.from_email,
        status: candidateIndex === -1 ? "rejected" : "success",
        summary: candidateIndex === -1 ? "Rejet (traitement manuel)" : "Société validée et créée",
        odoo_url: extractOdooLink(bodyText)
      });
    } else {
      showStatus(tabName, "error", `Erreur validation (HTTP ${response.status}).`);
    }
  } catch (err) {
    showStatus(tabName, "error", `Erreur : ${err.message || err}`);
  }
}

/* ===========================================================================
 * FORMULAIRE CRÉATION MANUELLE
 * ========================================================================= */
function renderManualForm(tabName, data, originalPayload) {
  hideLoader(tabName);
  const container = document.querySelector(`.tab-section[data-section="${tabName}"] .result-zone`);
  container.innerHTML = "";
  container.style.display = "";

  const ex = data.extracted || {};
  const ctxBody = (originalPayload.body || "").substring(0, 500);

  const wrap = document.createElement("div");
  wrap.className = "manual-form-wrap";
  wrap.innerHTML = `
    <div class="primo-section-title">Création manuelle dans Odoo</div>
    <p class="manual-form-help">Vérifiez et complétez les informations puis validez la création.</p>
    <form id="manual-form" class="manual-form">
      <fieldset>
        <legend>Société</legend>
        <label>Nom <span class="req">*</span>
          <input type="text" name="company_name" value="${escapeAttr(ex.company_name || "")}" required>
        </label>
        <label>TVA (BE0...)
          <input type="text" name="company_vat" value="${escapeAttr(ex.vat || "")}" placeholder="BE0123456789">
        </label>
        <label>Ville
          <input type="text" name="company_city" value="${escapeAttr(ex.city || "")}">
        </label>
        <label>Site web
          <input type="text" name="company_website" placeholder="https://...">
        </label>
      </fieldset>
      <fieldset>
        <legend>Contact</legend>
        <label>Nom <span class="req">*</span>
          <input type="text" name="person_name" value="${escapeAttr(ex.person_name || "")}" required>
        </label>
        <label>Email <span class="req">*</span>
          <input type="email" name="person_email" value="${escapeAttr(ex.person_email || "")}" required>
        </label>
        <label>Téléphone
          <input type="tel" name="person_phone" value="${escapeAttr(ex.person_phone || "")}">
        </label>
      </fieldset>
      <fieldset>
        <legend>Lead</legend>
        <label>Sujet
          <input type="text" name="lead_subject" value="${escapeAttr(originalPayload.subject || "")}">
        </label>
        <label>Description
          <textarea name="lead_description" rows="4">${escapeHtml(ex.context || ctxBody)}</textarea>
        </label>
      </fieldset>
      <div class="manual-form-actions">
        <button type="button" class="btn btn-secondary" id="manual-cancel">Annuler</button>
        <button type="submit" class="btn btn-primary">Créer dans Odoo</button>
      </div>
    </form>
  `;
  container.appendChild(wrap);

  document.getElementById("manual-cancel").addEventListener("click", () => {
    renderPrimoCandidates(tabName, data, originalPayload);
  });
  document.getElementById("manual-form").addEventListener("submit", (e) => {
    e.preventDefault();
    const formData = Object.fromEntries(new FormData(e.target));
    submitManualForm(tabName, data, formData, originalPayload);
  });
}

async function submitManualForm(tabName, data, formData, originalPayload) {
  showLoader(tabName, "Création manuelle dans Odoo en cours…");
  const url = "https://n8n.fdwebpro.be/webhook/pack5-validate-manual";
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        staging_id: data.staging_id,
        callback_token: data.callback_token,
        manual_data: formData
      })
    });
    const bodyText = await response.text();
    if (response.ok && bodyText.trim().startsWith("<")) {
      renderHtml(tabName, bodyText);
      addToHistory(CONFIG.primo.historyKey, {
        ts: Date.now(),
        mail_subject: originalPayload.subject,
        from_email: originalPayload.from_email,
        status: "success",
        summary: "Création manuelle validée",
        odoo_url: extractOdooLink(bodyText)
      });
    } else {
      showStatus(tabName, "error", `Erreur (HTTP ${response.status}). Le mail reste en attente.`);
    }
  } catch (err) {
    showStatus(tabName, "error", `Erreur : ${err.message || err}`);
  }
}

/* ===========================================================================
 * ONGLET CLIENT 360° (lookup partner Odoo + recherche)
 * ========================================================================= */
const CLIENT_WEBHOOK = "https://n8n.fdwebpro.be/webhook/pack5-client-360";
const CLIENT_SEARCH_WEBHOOK = "https://n8n.fdwebpro.be/webhook/pack5-client-search";
let _searchDebounce = null;

async function renderClientView() {
  const config = readConfig();
  if (!config.clientId) {
    showStatus("client", "error", "Configuration manquante — onglet Configuration : saisissez votre Client ID.");
    return;
  }

  const item = Office.context.mailbox.item;
  // Si pas de mail, on affiche directement le formulaire de recherche
  if (!item || !item.from) {
    renderClientSearchForm({ from_email: "", from_name: "" }, "Aucun mail sélectionné — recherchez un client par nom ou email.");
    return;
  }

  showLoader("client", "Chargement de la fiche client…");
  try {
    const payload = {
      from_email: item.from.emailAddress || "",
      from_name: item.from.displayName || "",
      subject: item.subject || ""
    };
    await loadClientCard(payload);
  } catch (err) {
    showStatus("client", "error", `Erreur : ${err.message || err}`);
  }
}

async function loadClientCard(payload) {
  const config = readConfig();
  showLoader("client", "Chargement de la fiche client…");

  const response = await fetch(CLIENT_WEBHOOK, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Pack5-Client-Id": config.clientId,
      "X-Pack5-Token": config.bearerToken,
      "X-Pack5-Source": "outlook-addin-v1.9",
      "X-Pack5-Action": "client_360"
    },
    body: JSON.stringify(payload)
  });
  const bodyText = await response.text();

  if (response.ok && bodyText.trim().startsWith("<")) {
    // Trouvé : affiche la fiche
    renderClientCard(bodyText, payload);
  } else if (response.status === 404) {
    // Pas trouvé : affiche le message + formulaire de recherche
    renderClientSearchForm(payload, null);
  } else {
    showStatus("client", "error", `Erreur HTTP ${response.status}.`);
  }
}

function renderClientCard(html, payload) {
  hideLoader("client");
  const section = document.querySelector('.tab-section[data-section="client"]');
  if (!section) return;
  const result = section.querySelector(".result-zone");
  const status = section.querySelector(".status-zone");
  if (status) status.style.display = "none";
  result.innerHTML = "";

  // Bouton "Voir un autre client" en haut
  const switchBtn = document.createElement("button");
  switchBtn.className = "btn btn-secondary btn-sm";
  switchBtn.textContent = "🔍 Voir un autre client";
  switchBtn.style.marginBottom = "8px";
  switchBtn.style.width = "100%";
  switchBtn.addEventListener("click", () => {
    renderClientSearchForm(payload || {}, null);
  });
  result.appendChild(switchBtn);

  // L'iframe avec la fiche
  const iframe = document.createElement("iframe");
  iframe.srcdoc = html;
  iframe.style.cssText = "width:100%;border:0;border-radius:6px;min-height:600px;";
  iframe.setAttribute("sandbox", "allow-scripts allow-same-origin allow-popups allow-top-navigation allow-forms");
  result.appendChild(iframe);
  result.style.display = "";
}

function renderClientSearchForm(payload, customMsg) {
  hideLoader("client");
  const section = document.querySelector('.tab-section[data-section="client"]');
  if (!section) return;
  const result = section.querySelector(".result-zone");
  const status = section.querySelector(".status-zone");
  if (status) status.style.display = "none";
  result.innerHTML = "";

  const wrap = document.createElement("div");
  wrap.className = "client-search-wrap";

  // Bloc info "client introuvable" si on vient d'un mail
  if (payload.from_email) {
    wrap.innerHTML += `
      <div class="client-not-found">
        <div class="client-not-found-icon">!</div>
        <h2>Client introuvable</h2>
        <p class="client-not-found-sub">Aucune fiche Odoo pour <code>${escapeHtml(payload.from_email)}</code></p>
        <p class="client-not-found-hint">Utilisez <strong>📨 Premier contact</strong> pour créer la fiche, ou recherchez un client existant ci-dessous.</p>
      </div>
    `;
  } else if (customMsg) {
    wrap.innerHTML += `<div class="client-search-intro">${escapeHtml(customMsg)}</div>`;
  }

  wrap.innerHTML += `
    <div class="client-search-box">
      <label class="client-search-label">Rechercher un client Odoo</label>
      <input type="text" id="client-search-input" placeholder="Nom de société, email, TVA..." autocomplete="off">
      <div id="client-search-results"></div>
    </div>
  `;
  result.appendChild(wrap);
  result.style.display = "";

  const input = document.getElementById("client-search-input");
  input.addEventListener("input", (e) => {
    const q = e.target.value.trim();
    if (_searchDebounce) clearTimeout(_searchDebounce);
    if (q.length < 2) {
      document.getElementById("client-search-results").innerHTML = "";
      return;
    }
    _searchDebounce = setTimeout(() => searchClient(q), 300);
  });
  input.focus();
}

async function searchClient(query) {
  const config = readConfig();
  const resultsDiv = document.getElementById("client-search-results");
  resultsDiv.innerHTML = '<div class="client-search-loading">Recherche…</div>';

  try {
    const response = await fetch(CLIENT_SEARCH_WEBHOOK, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Pack5-Client-Id": config.clientId,
        "X-Pack5-Token": config.bearerToken,
        "X-Pack5-Source": "outlook-addin-v1.8",
        "X-Pack5-Action": "client_search"
      },
      body: JSON.stringify({ q: query })
    });
    if (!response.ok) {
      resultsDiv.innerHTML = `<div class="client-search-error">Erreur HTTP ${response.status}</div>`;
      return;
    }
    let data = await response.json();
    if (Array.isArray(data)) data = data[0];
    const results = (data && data.results) || [];

    if (results.length === 0) {
      resultsDiv.innerHTML = '<div class="client-search-empty">Aucun client trouvé.</div>';
      return;
    }

    resultsDiv.innerHTML = "";
    results.forEach((r) => {
      const btn = document.createElement("button");
      btn.className = "client-search-result";
      btn.innerHTML = `
        <div class="client-search-name">${escapeHtml(r.name)}</div>
        <div class="client-search-meta">
          ${r.email ? '<span>' + escapeHtml(r.email) + '</span>' : ''}
          ${r.city ? '<span>· ' + escapeHtml(r.city) + '</span>' : ''}
          ${r.vat ? '<span>· ' + escapeHtml(r.vat) + '</span>' : ''}
        </div>
      `;
      btn.addEventListener("click", () => {
        loadClientCard({ partner_id: r.id, from_email: r.email, from_name: r.name });
      });
      resultsDiv.appendChild(btn);
    });
  } catch (err) {
    resultsDiv.innerHTML = `<div class="client-search-error">Erreur : ${escapeHtml(err.message || String(err))}</div>`;
  }
}

/* ===========================================================================
 * ONGLET FACTURE FOURNISSEUR (upload PDF vers Odoo)
 * ========================================================================= */
const INVOICE_WEBHOOK = "https://n8n.fdwebpro.be/webhook/pack5-upload-invoice";
const INVOICE_HISTORY_KEY = "pack5_invoice_history";

function renderInvoiceView() {
  const item = Office.context.mailbox.item;
  const section = document.querySelector('.tab-section[data-section="invoice"]');
  const result = section.querySelector(".result-zone");
  const statusZone = section.querySelector(".status-zone");

  if (statusZone) statusZone.style.display = "none";
  result.innerHTML = "";
  result.style.display = "";

  if (!item || !item.from) {
    result.innerHTML = '<div class="status status-info">Aucun mail sélectionné. Ouvrez un mail puis cliquez à nouveau.</div>';
    return;
  }

  // Loader pendant la récupération des PJ (Outlook peut mettre quelques ms
  // pour peupler item.attachments à la première ouverture du mail).
  result.innerHTML = '<div class="loader" style="display:flex"><div class="spinner"></div><div class="loader-text">Lecture des pièces jointes…</div></div>';

  // Récupération robuste des attachments avec retry (corrige le bug où il
  // fallait fermer/rouvrir l\'add-in pour voir les PJ).
  fetchInvoiceAttachments(item, (attachments) => {
    if (attachments.length === 0) {
      result.innerHTML = '<div class="status status-info">Aucune pièce jointe PDF dans ce mail. Cet onglet sert à envoyer une facture fournisseur PDF vers Odoo (création d\'un brouillon avec le PDF attaché).</div>';
      return;
    }

    result.innerHTML = "";
    const wrap = document.createElement("div");
    wrap.className = "invoice-wrap";
    wrap.innerHTML = `
      <div class="primo-section-title">Facture fournisseur à envoyer dans Odoo</div>
      <p class="invoice-help">Sélectionnez le PDF à envoyer. Un brouillon de facture fournisseur sera créé dans Odoo avec le PDF en pièce jointe.</p>
      <div class="invoice-from">
        <small>De : <strong>${escapeHtml(item.from.displayName || "")}</strong> &lt;${escapeHtml(item.from.emailAddress || "")}&gt;</small>
      </div>
    `;
    result.appendChild(wrap);

    attachments.forEach((att) => {
      const sizeKB = att.size ? Math.round(att.size / 1024) : "?";
      const btn = document.createElement("button");
      btn.className = "invoice-pdf-btn";
      btn.innerHTML = `
        <div class="invoice-pdf-icon">📄</div>
        <div class="invoice-pdf-info">
          <div class="invoice-pdf-name">${escapeHtml(att.name)}</div>
          <div class="invoice-pdf-size">${sizeKB} Ko</div>
        </div>
        <div class="invoice-pdf-arrow">→</div>
      `;
      btn.addEventListener("click", () => sendInvoice(att, item));
      wrap.appendChild(btn);
    });
  });
}

/**
 * Lit les pièces jointes PDF d\'un mail Outlook avec retry.
 *
 * Office.js peut renvoyer une liste vide à la première lecture si le mail
 * vient d\'être sélectionné et qu\'Outlook n\'a pas encore peuplé sa metadata.
 * On retente jusqu\'à 3 fois avec un délai croissant avant d\'abandonner.
 */
function fetchInvoiceAttachments(item, callback) {
  const filterPdf = (a) =>
    !a.isInline &&
    (a.contentType === "application/pdf" ||
      (a.name && a.name.toLowerCase().endsWith(".pdf")));

  const tryRead = (attempt) => {
    const list = (item.attachments || []).filter(filterPdf);
    if (list.length > 0 || attempt >= 3) {
      callback(list);
      return;
    }
    // Backoff : 100ms, 300ms, 800ms
    const delays = [100, 300, 800];
    setTimeout(() => tryRead(attempt + 1), delays[attempt] || 800);
  };

  tryRead(0);
}

async function sendInvoice(attachment, item) {
  showLoader("invoice", "Envoi de la facture vers Odoo…");

  const config = readConfig();
  if (!config.clientId) {
    showStatus("invoice", "error", "Configuration manquante — onglet Configuration : saisissez votre Client ID.");
    return;
  }

  try {
    // Récupérer le contenu du PDF en base64
    const pdfData = await new Promise((resolve, reject) => {
      item.getAttachmentContentAsync(attachment.id, (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error("Impossible de lire le PDF"));
          return;
        }
        resolve(result.value);
      });
    });

    const userProfile = (Office.context.mailbox && Office.context.mailbox.userProfile) || {};

    const payload = {
      action: "upload_invoice",
      from: item.from ? `${item.from.displayName || ""} <${item.from.emailAddress || ""}>`.trim() : "",
      from_name: item.from ? (item.from.displayName || "") : "",
      from_email: item.from ? (item.from.emailAddress || "") : "",
      outlook_user_email: userProfile.emailAddress || "",
      subject: item.subject || "",
      received_date: item.dateTimeCreated ? item.dateTimeCreated.toISOString() : null,
      message_id: item.internetMessageId || item.itemId || null,
      attachment: {
        filename: attachment.name,
        contentType: attachment.contentType || "application/pdf",
        content: pdfData.content,
        contentLength: attachment.size,
        format: pdfData.format || "base64"
      }
    };

    const response = await fetch(INVOICE_WEBHOOK, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Pack5-Client-Id": config.clientId,
        "X-Pack5-Token": config.bearerToken,
        "X-Pack5-Source": "outlook-addin-v1.6",
        "X-Pack5-Action": "upload_invoice"
      },
      body: JSON.stringify(payload)
    });
    const bodyText = await response.text();

    if (response.ok && bodyText.trim().startsWith("<")) {
      renderHtml("invoice", bodyText);
      addToHistory(INVOICE_HISTORY_KEY, {
        ts: Date.now(),
        mail_subject: item.subject || "?",
        from_email: item.from ? item.from.emailAddress || "?" : "?",
        status: "success",
        summary: `Facture envoyée : ${attachment.name}`,
        odoo_url: extractOdooLink(bodyText)
      });
    } else {
      showStatus("invoice", "error", `Erreur HTTP ${response.status}. Vérifiez le workflow W5.`);
      addToHistory(INVOICE_HISTORY_KEY, {
        ts: Date.now(),
        mail_subject: item.subject || "?",
        from_email: item.from ? item.from.emailAddress || "?" : "?",
        status: "server_error",
        summary: `Échec envoi : HTTP ${response.status}`
      });
    }
  } catch (err) {
    showStatus("invoice", "error", `Erreur : ${err.message || err}`);
  }
}

/* ===========================================================================
 * ONGLET HISTORIQUE
 * ========================================================================= */
function renderHistoryView() {
  // Met à jour les sous-onglets et la liste
  document.querySelectorAll(".history-subtab").forEach((b) => {
    b.classList.toggle("active", b.dataset.subtab === historySubTab);
  });
  // Récupère le bon storage key selon le sous-onglet courant
  let historyKey;
  if (historySubTab === "invoice") {
    historyKey = INVOICE_HISTORY_KEY;
  } else {
    historyKey = CONFIG[historySubTab].historyKey;
  }
  const list = readHistoryArr(historyKey);
  const ul = document.getElementById("history-list");
  ul.innerHTML = "";

  if (!list.length) {
    ul.innerHTML = '<li class="history-empty">Aucun envoi dans l\'historique</li>';
    return;
  }
  list.forEach((e) => {
    const li = document.createElement("li");
    li.className = `history-item history-${e.status}`;
    const time = new Date(e.ts).toLocaleString("fr-FR");
    const odooLink = e.odoo_url ? ` · <a href="${e.odoo_url}" target="_blank">Odoo</a>` : "";
    li.innerHTML = `
      <div class="history-subject">${escapeHtml(e.mail_subject || "(sans sujet)")}</div>
      <div class="history-meta">${escapeHtml(e.from_email || "?")} · ${time}${odooLink}</div>
      <div class="history-summary">${escapeHtml(e.summary || "")}</div>
    `;
    ul.appendChild(li);
  });
}

function bindHistoryActions() {
  document.querySelectorAll(".history-subtab").forEach((b) => {
    b.addEventListener("click", () => {
      historySubTab = b.dataset.subtab;
      renderHistoryView();
    });
  });

  const clearBtn = document.getElementById("history-clear");
  if (clearBtn) {
    clearBtn.addEventListener("click", () => {
      if (!confirm("Vider l'historique de tous les onglets (primo + suivi + facture) ?")) return;
      localStorage.removeItem(scopedKey(CONFIG.primo.historyKey));
      localStorage.removeItem(scopedKey(CONFIG.followup.historyKey));
      localStorage.removeItem(scopedKey(INVOICE_HISTORY_KEY));
      renderHistoryView();
    });
  }

  const exportBtn = document.getElementById("history-export");
  if (exportBtn) {
    exportBtn.addEventListener("click", exportData);
  }

  const importInput = document.getElementById("history-import");
  if (importInput) {
    importInput.addEventListener("change", (e) => {
      const file = e.target.files[0];
      if (file) importData(file);
      e.target.value = ""; // reset
    });
  }
}

/* ===========================================================================
 * EXPORT / IMPORT JSON (historique + config)
 * ========================================================================= */
function exportData() {
  const settings = Office.context.roamingSettings;
  const data = {
    exported_at: new Date().toISOString(),
    version: EXPORT_VERSION,
    history_primo: readHistoryArr(CONFIG.primo.historyKey),
    history_followup: readHistoryArr(CONFIG.followup.historyKey),
    history_invoice: readHistoryArr(INVOICE_HISTORY_KEY),
    config: {
      clientId: settings.get("clientId") || "",
      bearerToken: settings.get("bearerToken") || "",
      webhookUrl: settings.get("webhookUrl") || "",
      webhookFollowUpUrl: settings.get("webhookFollowUpUrl") || ""
    }
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `fdweb-pack5-export-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

function importData(file) {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = JSON.parse(e.target.result);

      if (Array.isArray(data.history_primo)) {
        localStorage.setItem(scopedKey(CONFIG.primo.historyKey), JSON.stringify(data.history_primo.slice(0, HISTORY_MAX)));
      }
      if (Array.isArray(data.history_followup)) {
        localStorage.setItem(scopedKey(CONFIG.followup.historyKey), JSON.stringify(data.history_followup.slice(0, HISTORY_MAX)));
      }
      if (Array.isArray(data.history_invoice)) {
        localStorage.setItem(scopedKey(INVOICE_HISTORY_KEY), JSON.stringify(data.history_invoice.slice(0, HISTORY_MAX)));
      }

      if (data.config) {
        const settings = Office.context.roamingSettings;
        let touched = false;
        if (typeof data.config.clientId === "string") { settings.set("clientId", data.config.clientId); touched = true; }
        if (typeof data.config.bearerToken === "string") { settings.set("bearerToken", data.config.bearerToken); touched = true; }
        if (typeof data.config.webhookUrl === "string") { settings.set("webhookUrl", data.config.webhookUrl); touched = true; }
        if (typeof data.config.webhookFollowUpUrl === "string") { settings.set("webhookFollowUpUrl", data.config.webhookFollowUpUrl); touched = true; }
        if (touched) {
          settings.saveAsync(() => {
            renderHistoryView();
            loadConfigForm();
            alert("Import terminé : historique et configuration mis à jour.");
          });
          return;
        }
      }
      renderHistoryView();
      alert("Import terminé : historique mis à jour.");
    } catch (err) {
      alert("Fichier JSON invalide : " + err.message);
    }
  };
  reader.readAsText(file);
}

/* ===========================================================================
 * ONGLET CONFIGURATION
 * ========================================================================= */
function bindConfigForm() {
  const form = document.getElementById("config-form");
  if (!form) return;
  form.addEventListener("submit", saveConfig);
  const testBtn = document.getElementById("config-test");
  if (testBtn) testBtn.addEventListener("click", testConfig);
}

function loadConfigForm() {
  const settings = Office.context.roamingSettings;
  document.getElementById("cfg-clientId").value = settings.get("clientId") || "";
  const tokField = document.getElementById("cfg-bearerToken");
  if (tokField) tokField.value = settings.get("bearerToken") || "";
  document.getElementById("cfg-webhookUrl").value = settings.get("webhookUrl") || CONFIG.primo.defaultUrl;
  document.getElementById("cfg-webhookFollowUpUrl").value = settings.get("webhookFollowUpUrl") || CONFIG.followup.defaultUrl;
  setConfigStatus("", "");
}

function saveConfig(e) {
  e.preventDefault();
  const clientId = document.getElementById("cfg-clientId").value.trim();
  const bearerToken = document.getElementById("cfg-bearerToken").value.trim();
  const webhookUrl = document.getElementById("cfg-webhookUrl").value.trim();
  const webhookFollowUpUrl = document.getElementById("cfg-webhookFollowUpUrl").value.trim();

  if (!clientId) { setConfigStatus("error", "Le Client ID est obligatoire."); return; }
  if (!bearerToken) { setConfigStatus("error", "Le Bearer Token est obligatoire (fourni par votre administrateur)."); return; }
  if (!isValidHttpsUrl(webhookUrl)) { setConfigStatus("error", "URL webhook Premier contact invalide (HTTPS requis)."); return; }
  if (!isValidHttpsUrl(webhookFollowUpUrl)) { setConfigStatus("error", "URL webhook Suivi de dossier invalide (HTTPS requis)."); return; }

  const settings = Office.context.roamingSettings;
  settings.set("clientId", clientId);
  settings.set("bearerToken", bearerToken);
  settings.set("webhookUrl", webhookUrl);
  settings.set("webhookFollowUpUrl", webhookFollowUpUrl);

  settings.saveAsync((result) => {
    if (result.status === Office.AsyncResultStatus.Succeeded) {
      setConfigStatus("success", "Configuration sauvegardée.");
    } else {
      setConfigStatus("error", "Erreur lors de la sauvegarde : " + (result.error ? result.error.message : "?"));
    }
  });
}

async function testConfig() {
  const clientId = document.getElementById("cfg-clientId").value.trim();
  const bearerToken = document.getElementById("cfg-bearerToken").value.trim();
  const webhookUrl = document.getElementById("cfg-webhookUrl").value.trim();
  if (!clientId) { setConfigStatus("error", "Saisissez d'abord un Client ID."); return; }
  if (!bearerToken) { setConfigStatus("error", "Saisissez d'abord un Bearer Token."); return; }
  if (!isValidHttpsUrl(webhookUrl)) { setConfigStatus("error", "URL webhook invalide."); return; }
  setConfigStatus("info", "Test en cours…");

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10000);
  try {
    const response = await fetch(webhookUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Pack5-Client-Id": clientId, "X-Pack5-Token": bearerToken, "X-Pack5-Source": "outlook-addin-v1.9-test" },
      body: JSON.stringify({ test: true, subject: "[TEST]", body: "Test de connexion", timestamp: new Date().toISOString() }),
      signal: controller.signal
    });
    clearTimeout(timer);
    if (response.ok) setConfigStatus("success", `Connexion OK (HTTP ${response.status}).`);
    else if (response.status >= 400 && response.status < 500) setConfigStatus("error", `Webhook joignable mais refus (HTTP ${response.status}).`);
    else setConfigStatus("error", `Erreur serveur (HTTP ${response.status}).`);
  } catch (err) {
    clearTimeout(timer);
    setConfigStatus("error", err.name === "AbortError" ? "Délai dépassé." : "Erreur : " + (err.message || err));
  }
}

function setConfigStatus(level, message) {
  const el = document.getElementById("config-status");
  if (!el) return;
  el.className = level ? `status status-${level}` : "";
  el.textContent = message;
  el.style.display = message ? "" : "none";
}

/* ===========================================================================
 * READ CONFIG (utilisé par les actions primo/followup)
 * ========================================================================= */
function readConfig() {
  const s = Office.context.roamingSettings;
  return {
    clientId: s.get("clientId") || "",
    bearerToken: s.get("bearerToken") || "",
    webhookUrl: s.get("webhookUrl") || CONFIG.primo.defaultUrl,
    webhookFollowUpUrl: s.get("webhookFollowUpUrl") || CONFIG.followup.defaultUrl
  };
}

/* ===========================================================================
 * BUILD PAYLOAD DEPUIS LE MAIL OUTLOOK
 * ========================================================================= */
async function buildPayload(item, action) {
  const [bodyText, bodyHtml, attachments] = await Promise.all([
    getBodyAsync(item, Office.CoercionType.Text),
    getBodyAsync(item, Office.CoercionType.Html),
    getAttachmentsAsync(item)
  ]);

  const fromAddress = item.from
    ? `${item.from.displayName || ""} <${item.from.emailAddress || ""}>`.trim()
    : "";

  const userProfile = (Office.context.mailbox && Office.context.mailbox.userProfile) || {};

  return {
    action,
    from: fromAddress,
    from_name: item.from ? (item.from.displayName || "") : "",
    from_email: item.from ? (item.from.emailAddress || "") : "",
    outlook_user_name: userProfile.displayName || "",
    outlook_user_email: userProfile.emailAddress || "",
    to: extractRecipients(item.to),
    cc: extractRecipients(item.cc),
    subject: item.subject || "",
    body: bodyText || "",
    html: bodyHtml || "",
    attachments,
    received_date: item.dateTimeCreated ? item.dateTimeCreated.toISOString() : null,
    message_id: item.internetMessageId || item.itemId || null,
    conversation_id: item.conversationId || null
  };
}

function extractRecipients(recipients) {
  if (!recipients || !recipients.length) return [];
  return recipients.map((r) => ({ name: r.displayName || "", email: r.emailAddress || "" }));
}

function getBodyAsync(item, coercionType) {
  return new Promise((resolve) => {
    item.body.getAsync(coercionType, (result) => {
      resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : "");
    });
  });
}

async function getAttachmentsAsync(item) {
  const list = item.attachments || [];
  if (!list.length) return [];
  const results = await Promise.all(list.map((att) => getOneAttachment(item, att)));
  return results.filter(Boolean);
}

function getOneAttachment(item, att) {
  return new Promise((resolve) => {
    if (att.isInline) { resolve(null); return; }
    item.getAttachmentContentAsync(att.id, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) { resolve(null); return; }
      const v = result.value;
      resolve({
        filename: att.name,
        contentType: att.contentType || "application/octet-stream",
        content: v.content,
        contentLength: att.size,
        format: v.format || "base64"
      });
    });
  });
}

/* ===========================================================================
 * UI HELPERS PAR ONGLET
 * ========================================================================= */
function showLoader(tabName, text) {
  const section = document.querySelector(`.tab-section[data-section="${tabName}"]`);
  if (!section) return;
  const loader = section.querySelector(".loader");
  const status = section.querySelector(".status-zone");
  const result = section.querySelector(".result-zone");
  if (status) { status.style.display = "none"; }
  if (result) { result.style.display = "none"; }
  if (loader) {
    loader.style.display = "flex";
    const t = loader.querySelector(".loader-text");
    if (t && text) t.textContent = text;
  }
}

function hideLoader(tabName) {
  const section = document.querySelector(`.tab-section[data-section="${tabName}"]`);
  if (!section) return;
  const loader = section.querySelector(".loader");
  if (loader) loader.style.display = "none";
}

function showStatus(tabName, level, message) {
  hideLoader(tabName);
  const section = document.querySelector(`.tab-section[data-section="${tabName}"]`);
  if (!section) return;
  const result = section.querySelector(".result-zone");
  const status = section.querySelector(".status-zone");
  if (result) result.style.display = "none";
  if (status) {
    status.style.display = "";
    status.className = `status status-${level} status-zone`;
    status.textContent = message;
  }
}

function renderHtml(tabName, html) {
  hideLoader(tabName);
  const section = document.querySelector(`.tab-section[data-section="${tabName}"]`);
  if (!section) return;
  const result = section.querySelector(".result-zone");
  const status = section.querySelector(".status-zone");
  if (status) status.style.display = "none";
  if (result) {
    result.innerHTML = "";
    const iframe = document.createElement("iframe");
    iframe.srcdoc = html;
    iframe.style.cssText = "width:100%;height:100%;min-height:540px;border:0;border-radius:6px";
    iframe.setAttribute("sandbox", "allow-scripts allow-same-origin allow-popups allow-top-navigation allow-forms");
    result.appendChild(iframe);
    result.style.display = "";
  }
}

/* ===========================================================================
 * HISTORIQUE — STORAGE
 * ========================================================================= */
/**
 * Scope une clé localStorage par email Outlook actif.
 * Sans ça, deux comptes mail dans le même Outlook partageraient le même historique
 * (localStorage est lié au domaine, pas à la mailbox).
 */
function scopedKey(key) {
  try {
    const email = (Office.context && Office.context.mailbox && Office.context.mailbox.userProfile && Office.context.mailbox.userProfile.emailAddress) || "default";
    return key + "__" + email.toLowerCase();
  } catch {
    return key;
  }
}

function readHistoryArr(key) {
  try {
    const raw = localStorage.getItem(scopedKey(key));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function writeHistoryArr(key, arr) {
  try { localStorage.setItem(scopedKey(key), JSON.stringify(arr.slice(0, HISTORY_MAX))); } catch {}
}

function addToHistory(key, entry) {
  const list = readHistoryArr(key);
  list.unshift(entry);
  writeHistoryArr(key, list);
  if (currentTab === "history") renderHistoryView();
}

/* ===========================================================================
 * UTILITAIRES
 * ========================================================================= */
function extractOdooLink(html) {
  if (!html || typeof html !== "string") return null;
  const m = html.match(/https?:\/\/[a-z0-9.-]+\.odoo\.com\/odoo\/crm\/\d+/);
  return m ? m[0] : null;
}

function escapeHtml(s) {
  return String(s || "").replace(/[<>&"']/g, (c) =>
    ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "\"": "&quot;", "'": "&#39;" }[c])
  );
}

function escapeAttr(s) { return escapeHtml(s); }

function isValidHttpsUrl(value) {
  try {
    const u = new URL(value);
    return u.protocol === "https:";
  } catch { return false; }
}
